/**
 * A session's title as people read it (pure, so it is tested). A session Orgus started for a
 * mission has the mission prompt as its first prompt ("[msn_...] Title Goal: ## Summary ..."):
 * it reads as the mission's title, never the raw prompt. Every other session keeps its first
 * prompt, stripped of Markdown.
 */
import type { Id, Session } from '@orgus/protocol';
import { plainText } from '../../lib/plainText.ts';

const MARKER = /^\s*\[(msn_[A-Za-z0-9_-]+)\]\s*/;

/** The mission id a session's title carries, if Orgus started it for a mission. */
export function missionIdOfTitle(title: string): Id | null {
  return MARKER.exec(title)?.[1] ?? null;
}

export interface ShownTitle {
  title: string;
  /** Set when a mission started the session. */
  missionId: Id | null;
}

/** The title to show: the mission's own title when known, else the prompt's first line without its marker. */
export function shownTitle(s: Pick<Session, 'title'>, missionTitle?: (id: Id) => string | undefined): ShownTitle {
  const missionId = missionIdOfTitle(s.title);
  if (!missionId) return { title: plainText(s.title).replace(/\s+/g, ' ').trim() || 'Untitled session', missionId: null };
  const known = missionTitle?.(missionId);
  if (known) return { title: known, missionId };
  const rest = s.title.replace(MARKER, '');
  const first = rest.split(/\n|\s+Goal:/)[0]?.trim();
  return { title: first || 'Mission session', missionId };
}

const INNER_MARKER = /\[(msn_[A-Za-z0-9_-]+)\]/;

/**
 * A feed line as people read it. An agent names a mission session by its first prompt, so its
 * events read "Waiting for you: [msn_...] Title Goal: ...": the mission's title replaces the
 * marker and the prompt after it, never a raw id (R-FN-8).
 */
export function feedTitle(title: string, missionTitle?: (id: Id) => string | undefined): string {
  const m = INNER_MARKER.exec(title);
  if (!m) return title;
  const head = title.slice(0, m.index);
  return `${head}${shownTitle({ title: title.slice(m.index) }, missionTitle).title}`;
}
